import { useAppDispatch, useAppSelector } from "../../../store";
import { useCallback, useContext, useEffect } from "react";
import { CustomerSpecificationsUpdate } from "../../../../../reducers/models/boardUpdates";
import {
  Board,
  BoardContext,
  clearCustomerSpecificationsUpdate,
  updateCustomerSpecifications,
} from "../../../../../reducers/uploadedBoard";
import { useDebouncedCallback } from "use-debounce";
import { useSendUpdateEvent } from "./sendUpdates";
import {
  BoardTransitionEvent,
  MachineGroup,
} from "../state-machine/sendBoardEvent";
import { shallowEqual } from "react-redux";
import {
  BoardIdContext,
  BoardShipmentIds,
} from "../state-machine/reviewBoardFlowLoader";
import useRefreshShipments from "../state-machine/shipments";

export function useSendCustomerSpecificationsEvents() {
  const { boardId } = useContext(BoardIdContext) as BoardShipmentIds;
  const { refreshShipments } = useRefreshShipments();
  const { sendUpdateEvent } = useSendUpdateEvent(refreshShipments);
  const dispatch = useAppDispatch();

  const customerSpecificationsUpdate = useAppSelector(
    (state) =>
      state.uploadedBoard.boards[boardId]?.customerSpecificationsUpdate || null,
    shallowEqual,
  );

  const debouncedUpdateCustomerSpecifications = useDebouncedCallback(
    (update: CustomerSpecificationsUpdate) => {
      void sendUpdateEvent({
        machine_group: MachineGroup.BOARD,
        model_id: boardId,
        event: BoardTransitionEvent.SHOULD_UPDATE_CUSTOMER_SPECIFICATIONS,
        update,
      });
    },
    1000,
    { leading: true },
  );

  useEffect(() => {
    if (customerSpecificationsUpdate !== null && boardId) {
      debouncedUpdateCustomerSpecifications(customerSpecificationsUpdate);
      dispatch(clearCustomerSpecificationsUpdate({ boardId }));
    }
  }, [
    customerSpecificationsUpdate,
    debouncedUpdateCustomerSpecifications,
    dispatch,
    boardId,
  ]);
}

export function useUpdateCustomerSpecifications() {
  const { boardId } = useContext(BoardIdContext) as BoardShipmentIds;
  const dispatch = useAppDispatch();
  const customerSpecifications = useAppSelector(
    (state) =>
      ((state.uploadedBoard.boards[boardId] as BoardContext).board as Board)
        .manufacturing_details.customer_specifications,
  );
  const update = useCallback(
    (customerSpecificationsUpdate: CustomerSpecificationsUpdate) => {
      if (boardId && customerSpecifications) {
        dispatch(
          updateCustomerSpecifications({
            boardId,
            update: customerSpecificationsUpdate,
          }),
        );
      }
    },
    [dispatch, boardId, customerSpecifications],
  );

  const updateXrayInspection = useCallback(
    (xrayInspection: boolean) => {
      update({
        ...customerSpecifications,
        xray_inspection: xrayInspection,
      });
    },
    [customerSpecifications, update],
  );

  const updateCleaning = useCallback(
    (cleaning: boolean) => {
      update({
        ...customerSpecifications,
        cleaning: cleaning,
      });
    },
    [customerSpecifications, update],
  );

  const updateNotes = useCallback(
    (notes: string) => {
      update({
        ...customerSpecifications,
        notes: notes,
      });
    },
    [customerSpecifications, update],
  );

  const updateConformalCoating = useCallback(
    (conformalCoating: boolean) => {
      update({
        ...customerSpecifications,
        conformal_coating: conformalCoating,
      });
    },
    [customerSpecifications, update],
  );

  const updateIncludeDateCode = useCallback(
    (includeDateCode: boolean) => {
      update({
        ...customerSpecifications,
        include_date_code: includeDateCode,
      });
    },
    [customerSpecifications, update],
  );

  return {
    updateXrayInspection,
    updateCleaning,
    updateNotes,
    updateConformalCoating,
    updateIncludeDateCode,
  };
}
